import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Inline,
  BoxSizeOptions,
  BoxBorderOptions,
  JustifyContentStyleOptions,
} from '@styleseat/ui';
import { colors } from '@styleseat/brand';
import store from '../../../store';
import analytics from '../../../modules/analytics';
import {
  useAsyncEffect,
  useUserState,
  useMount,
} from '../../../hooks';
import {
  Button,
  HeaderText,
  NewBadge,
  Stack,
} from '../../ui';
import withStore from '../../withStore';
import ProgressBar from './ProgressBar';
import { IRootDispatch, RootState } from '../../../store/models';
import { PrivilegedProvider } from '../../../api/Providers';
import { SearchRankState } from '../../../store/ProviderSearchRank.model';

const LAST_INTERACTION_TIME_KEY = 'search_ranking_last_interaction';
const VISIBILITY_TRACKING_KEY = 'provider_search_rank_home_tab_view';
const VISIBILITY_BADGE_TRACKING_KEY = 'provider_search_rank_home_tab_badge_view';
const BUTTON_CLICK_KEY = 'provider_search_rank_cta_click';

export type GenericSearchRankBannerProps = {
  provider: PrivilegedProvider;
  hideButton?: boolean;
  shouldTrack?: boolean;
  hideNewBadge?: boolean;
  alignment?: JustifyContentStyleOptions;
  barHeight?: BoxSizeOptions;
  paddingX?: BoxSizeOptions;
  paddingTop?: BoxSizeOptions;
  paddingBottom?: BoxSizeOptions;
  borderWidth?: BoxBorderOptions;
  showHeader?: boolean;
};

export const GenericSearchRankBanner: React.FC<GenericSearchRankBannerProps> = ({
  provider,
  hideButton,
  hideNewBadge,
  shouldTrack,
  alignment,
  barHeight,
  paddingX,
  paddingTop,
  paddingBottom,
  borderWidth,
  showHeader = true,
}) => {
  const [month, setMonth] = useState<string>();
  const dispatch = useDispatch<IRootDispatch>();
  const {
    stats,
    eligible,
  }: SearchRankState = useSelector<RootState, SearchRankState>(
    store.select.providerSearchRank.pageData,
  );

  useMount(() => {
    dispatch.providerSearchRank.calculateEligibility();
    dispatch.providerSearchRank.calculateStats({ providerId: provider.id });
    const now = new Date(Date.now());
    // second parameter ensures we don't re-increment a month due to insufficient days in a month
    now.setMonth(now.getMonth() - 1, 1);
    setMonth(now.toLocaleString('default', { month: 'long' }));
  });

  // Badge Display Handling
  // Make sure to check only if we can see the whole card (ie: showProStats == true)
  const [showBadge, setBadge] = useState(false);
  const [
    interactionTime,
    setInteractionTime,
    interactionTimeLoaded,
  ] = useUserState<string>(LAST_INTERACTION_TIME_KEY);
  useAsyncEffect(async () => {
    if (interactionTimeLoaded && !interactionTime && eligible && !hideNewBadge) {
      analytics.track(VISIBILITY_BADGE_TRACKING_KEY);
      const now = new Date();
      setInteractionTime(now.toISOString());
      setBadge(true);
    }
  }, [
    interactionTime,
    interactionTimeLoaded,
    eligible,
  ]);

  // track page view
  useEffect(() => {
    if (eligible && stats && shouldTrack) {
      analytics.track(VISIBILITY_TRACKING_KEY);
    }
  }, [
    eligible,
    stats,
  ]);

  if (!eligible || !stats) {
    return null;
  }

  return (
    <Stack
      testID="search-ranking-container"
      space={16}
      minWidth="100%"
      paddingTop={paddingTop === undefined ? 24 : paddingTop}
      paddingX={paddingX === undefined ? 24 : paddingX}
      paddingBottom={paddingBottom === undefined ? 32 : paddingBottom}
      borderWidth={borderWidth === undefined ? 0.5 : borderWidth}
      borderColor={colors.lightGrey}
    >
      {showHeader && (
        <Inline justifyContent={alignment || 'center'} space={4}>
          {showBadge && (
            <NewBadge testID="search-ranking-container-new-badge" text="New" size="small" />
          )}
          <HeaderText level={3} testID="search-ranking-container-month-text">
            {month} Search Ranking
          </HeaderText>
        </Inline>
      )}
      <ProgressBar
        url={provider.profile_photo}
        criteriaSatisfied={stats.criteriaSatisfied}
        maxHeight={barHeight}
      />
      {!hideButton && (
        <Box alignItems="center" alignSelf="center">
          <Button
            link="provider.search_rank"
            clickTracking={BUTTON_CLICK_KEY}
            appearance="primary-neue"
            testID="search-ranking-container-cta"
            text="Boost Your Search Ranking"
          />
        </Box>
      )}
    </Stack>
  );
};

export default withStore(GenericSearchRankBanner);
